/**
 * True infinite carousel using clone-based wrapping.
 *
 * Track layout:
 *   [start clones: last N real slides] [real slides 0..count-1] [end clones: first N real slides]
 *
 * where N = perView. Absolute index (absIndex) addresses any position in this
 * extended track. After a transition into a clone zone, we silently jump to the
 * equivalent real position so the next navigation is seamless.
 */
class TrustBadgesCarousel extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement|null} */
    this.track = null;
    /** @type {HTMLElement[]} */
    this.slides = [];
    /** @type {HTMLButtonElement|null} */
    this.btnPrev = null;
    /** @type {HTMLButtonElement|null} */
    this.btnNext = null;
    /** @type {HTMLElement|null} */
    this.dotsContainer = null;
    /** @type {HTMLElement|null} */
    this.liveRegion = null;
    /** @type {HTMLButtonElement|null} */
    this.btnPause = null;
    this.perViewDesktop = 4;
    this.perViewMobile = 3;
    this.autoplayDelay = 0;
    this.currentAbsIndex = 0;
    this.currentRealIndex = 0;
    this.isPaused = false;
    /** @type {ReturnType<typeof setInterval>|null} */
    this.autoplayTimer = null;
    /** @type {ReturnType<typeof setTimeout>|null} */
    this.resizeTimer = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.resizeHandler = () => {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.handleResize(), 200);
    };
    this.transitionEndHandler = () => this.handleTransitionEnd();
  }

  connectedCallback() {
    this.track = /** @type {HTMLElement|null} */ (this.querySelector('[data-tb-track]'));
    // Capture real slides BEFORE clones are injected
    this.slides = Array.from(/** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-tb-slide]')));
    this.btnPrev = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-tb-prev]'));
    this.btnNext = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-tb-next]'));
    this.dotsContainer = /** @type {HTMLElement|null} */ (this.querySelector('[data-tb-dots]'));
    this.liveRegion = /** @type {HTMLElement|null} */ (this.querySelector('[data-tb-live]'));
    this.btnPause = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-tb-pause]'));

    this.perViewDesktop = parseInt(this.dataset.perViewDesktop ?? '4', 10);
    this.perViewMobile = parseInt(this.dataset.perViewMobile ?? '3', 10);
    this.autoplayDelay = parseInt(this.dataset.autoplayDelay ?? '0', 10);

    this.btnPrev?.addEventListener('click', () => { this.resetAutoplay(); this.prev(); });
    this.btnNext?.addEventListener('click', () => { this.resetAutoplay(); this.next(); });
    this.btnPause?.addEventListener('click', () => this.togglePause());
    this.track?.addEventListener('transitionend', this.transitionEndHandler);

    this.addEventListener('mouseenter', () => { if (!this.isPaused) this.stopAutoplay(); });
    this.addEventListener('mouseleave', () => { if (!this.isPaused) this.startAutoplay(); });
    this.addEventListener('focusin',    () => { if (!this.isPaused) this.stopAutoplay(); });
    this.addEventListener('focusout',   () => { if (!this.isPaused) this.startAutoplay(); });

    window.addEventListener('resize', this.resizeHandler);

    this.buildDots();
    this.buildClones();
    this.setPosition(this.initialAbsIndex(), false);

    if (!this.prefersReducedMotion) this.startAutoplay();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
    this.track?.removeEventListener('transitionend', this.transitionEndHandler);
    this.stopAutoplay();
  }

  // ─── Getters ────────────────────────────────────────────────────────────────

  get perView() {
    return window.innerWidth <= 749 ? this.perViewMobile : this.perViewDesktop;
  }

  get slideCount() {
    return this.slides.length;
  }

  get hasMultiplePages() {
    return this.slideCount > this.perView;
  }

  // First real slide sits at absIndex = perView (after start clones)
  initialAbsIndex() {
    return this.hasMultiplePages ? this.perView : 0;
  }

  // ─── Clone management ───────────────────────────────────────────────────────

  buildClones() {
    if (!this.track) return;

    // Remove any previously injected clones
    this.track.querySelectorAll('[data-tb-clone]').forEach(el => el.remove());
    if (!this.hasMultiplePages) return;

    const perView = this.perView;
    const count = this.slideCount;

    // END clones: copies of the first perView real slides, appended after the last real slide
    const endFrag = document.createDocumentFragment();
    for (let i = 0; i < perView; i++) {
      const source = this.slides[i % count];
      if (!source) continue;
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      clone.setAttribute('data-tb-clone', 'end');
      clone.setAttribute('aria-hidden', 'true');
      endFrag.appendChild(clone);
    }
    this.track.appendChild(endFrag);

    // START clones: copies of the last perView real slides, prepended before the first real slide
    const startFrag = document.createDocumentFragment();
    for (let i = 0; i < perView; i++) {
      const realIdx = (count - perView + i) % count;
      const source = this.slides[realIdx];
      if (!source) continue;
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      clone.setAttribute('data-tb-clone', 'start');
      clone.setAttribute('aria-hidden', 'true');
      startFrag.appendChild(clone);
    }
    this.track.insertBefore(startFrag, this.track.firstChild);
  }

  // ─── Position ───────────────────────────────────────────────────────────────

  /**
   * Move the track to absIndex.
   * @param {number} absIndex
   * @param {boolean} animate
   */
  setPosition(absIndex, animate = true) {
    if (!this.track) return;

    if (!animate) this.track.style.transition = 'none';

    this.currentAbsIndex = absIndex;
    this.currentRealIndex = this.hasMultiplePages
      ? ((absIndex - this.perView) % this.slideCount + this.slideCount) % this.slideCount
      : 0;

    this.track.style.transform = `translateX(-${(absIndex / this.perView) * 100}%)`;

    if (!animate) {
      // Force layout so transition:none is applied before restoring the rule
      this.track.getBoundingClientRect();
      this.track.style.transition = '';
    }

    this.updateDots();
    this.announce();
  }

  // After animating into a clone zone, silently snap back to the real equivalent
  handleTransitionEnd() {
    const perView = this.perView;
    const count = this.slideCount;
    const abs = this.currentAbsIndex;

    if (abs >= perView + count) {
      // Landed in end clones → jump to the matching real position near the start
      this.setPosition(perView + (abs - (perView + count)), false);
    } else if (abs < perView) {
      // Landed in start clones → jump to the matching real position near the end
      this.setPosition(count + abs, false);
    }
  }

  handleResize() {
    const realIdx = this.currentRealIndex;
    this.buildDots();
    this.buildClones();
    this.setPosition(this.hasMultiplePages ? realIdx + this.perView : 0, false);
  }

  // ─── Navigation ─────────────────────────────────────────────────────────────

  prev() { this.setPosition(this.currentAbsIndex - 1); }
  next() { this.setPosition(this.currentAbsIndex + 1); }

  // ─── Autoplay ────────────────────────────────────────────────────────────────

  startAutoplay() {
    if (!this.autoplayDelay || !this.hasMultiplePages || this.prefersReducedMotion) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
  }

  resetAutoplay() {
    if (this.isPaused) return;
    this.stopAutoplay();
    this.startAutoplay();
  }

  togglePause() {
    this.isPaused = !this.isPaused;
    this.isPaused ? this.stopAutoplay() : this.startAutoplay();
    if (this.btnPause) {
      this.btnPause.setAttribute('aria-pressed', String(this.isPaused));
      this.btnPause.setAttribute('aria-label', this.isPaused ? 'Play carousel' : 'Pause carousel');
    }
  }

  // ─── Dots ────────────────────────────────────────────────────────────────────

  buildDots() {
    if (!this.dotsContainer) return;
    const visible = this.hasMultiplePages;
    this.dotsContainer.innerHTML = '';
    this.dotsContainer.hidden = !visible;
    if (this.btnPrev) this.btnPrev.hidden = !visible;
    if (this.btnNext) this.btnNext.hidden = !visible;

    for (let i = 0; i < this.slideCount; i++) {
      const dot = document.createElement('button');
      dot.className = 'tb-dot';
      dot.setAttribute('type', 'button');
      dot.setAttribute('aria-label', `Go to badge ${i + 1}`);
      dot.addEventListener('click', () => {
        this.resetAutoplay();
        this.setPosition(i + this.perView);
      });
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    this.dotsContainer?.querySelectorAll('.tb-dot').forEach((dot, i) => {
      const isCurrent = i === this.currentRealIndex;
      dot.classList.toggle('is-active', isCurrent);
      dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });
  }

  // ─── Screen reader announcement ─────────────────────────────────────────────

  announce() {
    if (!this.liveRegion) return;
    // Clear first so identical consecutive values are still re-announced
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = `Badge ${this.currentRealIndex + 1} of ${this.slideCount}`;
      }
    });
  }
}

customElements.define('trust-badges-carousel', TrustBadgesCarousel);
